'use client';

import { useEffect, useId, useRef } from 'react';
import { PortalLink as Link, t } from '@akademi/site-builder-runtime';
import type { MenuEntry, ShellConfig } from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { ChromeStyles } from './ChromeStyles';
import type { SolarChromeProps } from './types';
import { headerSearchFor } from './SolarHeaderSearch';

/**
 * The departments, as a band of links under the header.
 *
 * <h3>Why a shop wants this</h3>
 *
 * <p>The merchants this shop is measured against — Toolstation, Screwfix, CEF —
 * all put their departments on a coloured bar under the lockup, and none of
 * them hide the range behind a "Shop" dropdown. The reason is not decoration:
 * a visitor who cannot see that the shop sells cable, protection and mounting
 * without hovering something does not know the range is real. The categories
 * were already authored here; they were just one interaction away from being
 * read.
 *
 * <h3>Why this is markup and not CSS</h3>
 *
 * <p>An earlier attempt built this by injecting CSS that wrapped the header's
 * own nav onto a second row with negative side margins. It broke the live
 * header twice, the second time on a trading shop, because the header's layout
 * is INLINE styles on elements no theme owns — every rule is a specificity
 * fight whose outcome cannot be read from the markup, and this workstation
 * runs no browser to judge it in. That note concluded the job needed a
 * `headerBottom` slot on `SiteShellPresenter`.
 *
 * <p><strong>That slot already exists.</strong> `headerCategorySlot` renders
 * its content in a full-width strip between the header and `main`, and the
 * base's own comment says the slot content owns its internal layout. So this
 * is ordinary markup in a place the shell put there for it, with classes only
 * this theme uses — nothing overrides anything, and the one wrapper rule in
 * {@link ChromeStyles} targets a single element by its own content.
 *
 * <h3>Where the links come from</h3>
 *
 * <p>The operator's own megamenu, flattened. Nothing is fetched and nothing is
 * invented: whatever they put under "Shop" in the Shell editor is what appears
 * on the bar, in their order, and editing the menu moves the bar with it. The
 * column labels ("Generate", "Install") are dropped — the bar is one row of
 * departments, which is the shape being copied, and the grouping only ever
 * existed to organise a dropdown panel.
 */

/** The megamenu links of the first entry that has one, flattened in order. */
export function departmentsFrom(
  config: ShellConfig,
  locale: string,
  fallback: string,
): Array<{ label: string; href: string }> {
  const menu = (config.header?.menu ?? []) as MenuEntry[];
  for (const entry of menu) {
    const columns = (entry as { megamenu?: { columns?: unknown[] } }).megamenu?.columns;
    if (!Array.isArray(columns) || columns.length === 0) continue;
    const links = columns
      .flatMap((column) => (column as { links?: unknown[] }).links ?? [])
      .map((link) => {
        const { label, href } = link as { label?: Parameters<typeof t>[0]; href?: string };
        return { label: t(label, locale, fallback), href: href ?? '' };
      })
      // A link with no href is an operator half-finishing an entry; it would
      // render as a department that goes nowhere.
      .filter((link) => link.label && link.href);
    if (links.length) return links;
  }
  return [];
}

/**
 * The operator's other top-level menu links — Design your system, Trade accounts, Delivery,
 * Help — in their order. The bar carries them on its right, because on a wide screen the
 * header row no longer does: the approved mockup (27 September 2026) has one header row of
 * logo, search and three actions, and the departments and these links on the band beneath.
 * The header's own menu stays in the markup for the mobile drawer; `ChromeStyles` only hides
 * its desktop row.
 */
export function menuLinksFrom(
  config: ShellConfig,
  locale: string,
  fallback: string,
): Array<{ label: string; href: string }> {
  const menu = (config.header?.menu ?? []) as MenuEntry[];
  return menu
    .filter((entry) => {
      const columns = (entry as { megamenu?: { columns?: unknown[] } }).megamenu?.columns;
      return !(Array.isArray(columns) && columns.length > 0);
    })
    .map((entry) => {
      const { label, href } = entry as { label?: Parameters<typeof t>[0]; href?: string };
      return { label: t(label, locale, fallback), href: href ?? '' };
    })
    .filter((link) => link.label && link.href);
}

/** How many of the operator's top-level links the bar carries beside the departments. */
export const BAR_LINKS = 2;

export function SolarDepartmentBar({ config, locale, localeFallback }: SolarChromeProps) {
  const label = useSolarText();
  const departments = departmentsFrom(config, locale, localeFallback);
  const menuLinks = menuLinksFrom(config, locale, localeFallback);
  const links = menuLinks.slice(0, BAR_LINKS);
  const search = headerSearchFor(config, locale, localeFallback);
  const menu = useRef<HTMLDetailsElement>(null);
  const searchId = useId();

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (menu.current?.open && !menu.current.contains(event.target as Node)) menu.current.open = false;
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);
  // A shop whose operator authored no megamenu gets no bar rather than an
  // empty coloured band.
  if (!departments.length) return null;

  return (
    <nav className="ae ae-departments" aria-label={label('chrome.departments')}>
      <ChromeStyles />
      {search ? <form className="ae-mobile-search" action={search.resultsHref} method="get" role="search">
        <label className="ae-visually-hidden" htmlFor={searchId}>{label('chrome.search')}</label>
        <input id={searchId} type="search" name="q" placeholder={search.placeholder || label('chrome.search')} required />
        <button type="submit" aria-label={label('chrome.search')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>
        </button>
      </form> : null}
      <div className="ae-departments__inner">
        <details ref={menu} className="ae-department-menu" onKeyDown={(event) => {
          if (event.key === 'Escape' && menu.current?.open) {
            event.preventDefault();
            menu.current.open = false;
            menu.current.querySelector('summary')?.focus();
          }
        }} onClick={(event) => {
          if ((event.target as Element).closest('a') && menu.current) menu.current.open = false;
        }}>
          <summary>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
            {label('chrome.allProducts')}<span className="ae-department-menu__chevron" aria-hidden="true">⌄</span>
          </summary>
          <div className="ae-department-menu__panel">
            <p>{label('chrome.departments')}</p>
            <ul>{departments.map((department) => <li key={department.href}>
              <Link href={department.href}>{department.label}<span aria-hidden="true">→</span></Link>
            </li>)}</ul>
            {menuLinks.length ? <div className="ae-department-menu__help">
              {menuLinks.map((link) => <Link key={link.href} href={link.href}>{link.label}</Link>)}
            </div> : null}
          </div>
        </details>
        <ul className="ae-departments__list">
        {departments.slice(0, 4).map((department) => (
          <li className="ae-departments__featured" key={`${department.href}:${department.label}`}>
            <Link className="ae-departments__link" href={department.href}>
              {department.label}
            </Link>
          </li>
        ))}
        {links.map((link, index) => (
          <li
            key={`menu:${link.href}:${link.label}`}
            className={index === 0 ? 'ae-departments__aside ae-departments__aside--first' : 'ae-departments__aside'}
          >
            <Link className="ae-departments__link ae-departments__link--aside" href={link.href}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
      </div>
    </nav>
  );
}
